/**
 * Rich Text Accordion (Sales Locations, etc.)
 * Same approach as key-ingredients-accordion:
 * - Only one item open at a time
 * - Smooth open/close animation
 * - Uses capture phase to intercept clicks before collapsible-content
 */

class RichTextAccordion {
  constructor(container) {
    this.container = container || document.querySelector('.rich-text-accordion');
    if (!this.container) return;

    this.details = this.container.querySelectorAll('collapsible-content details');
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.animationDuration = 300;
    this.init();
  }

  init() {
    this.details.forEach((detail, index) => {
      const summary = detail.querySelector('summary');
      const content = detail.querySelector('[data-content]');
      if (!summary || !content) return;

      detail._accordion = { summary, content, animation: null, isClosing: false, isExpanding: false };

      summary.addEventListener('click', (e) => this.handleClick(e, detail), true);
    });

    // Ensure only first item is open on load (accordion rule)
    const openCount = Array.from(this.details).filter((d) => d.open).length;
    if (openCount > 1) {
      this.details.forEach((detail, index) => {
        if (index > 0 && detail.open) {
          detail.removeAttribute('open');
          detail.style.height = '';
          detail.style.overflow = '';
        }
      });
    }
  }

  handleClick(e, detail) {
    if (!this.container.contains(detail)) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    const { isClosing, isExpanding } = detail._accordion;
    if (isClosing || isExpanding) return;

    if (detail.open) {
      this.collapse(detail);
    } else {
      this.details.forEach((d) => {
        if (d !== detail && d.open) this.collapse(d);
      });
      this.expand(detail);
    }
  }

  collapse(detail) {
    const { summary, content, animation } = detail._accordion;
    if (!content) return;

    detail._accordion.isClosing = true;
    detail.style.overflow = 'hidden';
    const startHeight = `${detail.offsetHeight}px`;
    const endHeight = `${summary.offsetHeight}px`;

    if (animation) animation.cancel();

    if (!this.prefersReducedMotion.matches) {
      detail._accordion.animation = detail.animate(
        { height: [startHeight, endHeight] },
        { duration: this.animationDuration, easing: 'ease' }
      );
      detail._accordion.animation.onfinish = () => this.onAnimationFinish(detail, false);
      detail._accordion.animation.oncancel = () => (detail._accordion.isClosing = false);
    } else {
      this.onAnimationFinish(detail, false);
    }
  }

  expand(detail) {
    const { summary, content, animation } = detail._accordion;
    if (!content) return;

    detail.style.overflow = 'hidden';
    detail.style.height = `${detail.offsetHeight}px`;
    detail.open = true;
    requestAnimationFrame(() => {
      detail._accordion.isExpanding = true;
      const startHeight = detail.style.height;
      const endHeight = `${summary.offsetHeight + content.offsetHeight}px`;

      if (animation) detail._accordion.animation?.cancel();

      if (!this.prefersReducedMotion.matches) {
        detail._accordion.animation = detail.animate(
          { height: [startHeight, endHeight] },
          { duration: this.animationDuration, easing: 'ease' }
        );
        detail._accordion.animation.onfinish = () => this.onAnimationFinish(detail, true);
        detail._accordion.animation.oncancel = () => (detail._accordion.isExpanding = false);
      } else {
        this.onAnimationFinish(detail, true);
      }
    });
  }

  onAnimationFinish(detail, open) {
    detail.open = open;
    detail._accordion.animation = null;
    detail._accordion.isClosing = false;
    detail._accordion.isExpanding = false;
    detail.style.height = '';
    detail.style.overflow = '';
  }
}

function initRichTextAccordions() {
  document.querySelectorAll('.rich-text-accordion').forEach((container) => {
    new RichTextAccordion(container);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initRichTextAccordions);
} else {
  initRichTextAccordions();
}

document.addEventListener('shopify:section:load', initRichTextAccordions);
